import { Timestamp } from "firebase/firestore"
import { createContext, ReactNode, useContext, useState } from "react";
import { FormatDate, FormatSwedishShortDate, ParseDay } from "../hooks/timestamp";
import { useUsers } from "./users";
import { Workout } from "./workouts";
import { useWorkoutSettings } from "./workout-settings";

interface FilterContext {
  hasFilter: boolean
  setHasFilter: React.Dispatch<React.SetStateAction<boolean>>
  filters: Filter[]
}

interface Filter {
  name: string
  data: string[]
  filteredData: string[]
  setData: React.Dispatch<React.SetStateAction<string[]>>
  error?: string | null
  loading?: boolean | undefined,
  filterAddCallback: (v: string[]) => (void)
  filterMatchCallBack: (v: Workout, f: string) => (boolean)
}

const Filters = createContext({} as FilterContext);

export default function FilterProvider({ children }: { children: ReactNode }) { 

  const { usersExt, loading, error } = useUsers()
  const { activeWorkoutTypes, getWorkoutTypeName } = useWorkoutSettings()
  const [ hasFilter, setHasFilter ] = useState(false)
  const [ workoutTypes, setWorkouTypes] = useState<string[]>([])
  const [ coaches, setCoaches] = useState<string[]>([])
  const [ tenDays, setTenDays] = useState<string[]>([])

  const filters: Filter[] = []

  const getWorkoutTypes = () => {
    return activeWorkoutTypes.map((type) => type.name)
  }

  const getCoaches = () => {
    return usersExt.filter(user => user.isCoach).map(user => user.displayName);
  }
  
  const getTenDays = () => {
    const dates: Date[] = []
    for (let i = 0; i < 9; i++) {
      if (i === 0) dates.push(new Date())
      const date = new Date(dates[i])
      date.setDate(date.getDate() + 1)
      dates.push(date)
    }
    return dates.map((date) => {
      if (date.getDate() === new Date().getDate()) return "Idag"
      if (date.getDate() === new Date().getDate() + 1) return "Imorgon"
      return ParseDay(date.getDay()) + " " + date.getDate() + "/" + (date.getMonth() + 1)
    })
  }
  
  function matchDates(filterDate: string, workoutDate: Timestamp) {
    return FormatSwedishShortDate(filterDate) === FormatDate(workoutDate.toDate())
  }

  const workoutTypesFilter: Filter = {
    name: "Typ",
    data: getWorkoutTypes(),
    filteredData: workoutTypes,
    setData: setWorkouTypes,
    filterAddCallback: ((types: string[]) => setWorkouTypes(types)),
    filterMatchCallBack: (w: Workout, f: string) => getWorkoutTypeName(w.type) === f
  }

  const coachesFilter: Filter = {
    name: "Tränare",
    data: getCoaches(),
    filteredData: coaches,
    setData: setCoaches,
    loading: loading,
    error: error,
    filterAddCallback: ((types: string[]) => setCoaches(types)),
    filterMatchCallBack: (w: Workout, f: string) => w.coaches.some((c) => c.displayName === f)
  }

  const tenDaysFilter: Filter = {
    name: "Dag",
    data: getTenDays(),
    filteredData: tenDays,
    setData: setTenDays,
    filterAddCallback: ((types: string[]) => setTenDays(types)),
    filterMatchCallBack: (w: Workout, f: string) => matchDates(f, w.startTime)
  }

  filters.push(workoutTypesFilter)
  filters.push(coachesFilter)
  filters.push(tenDaysFilter)

  return (
    <Filters.Provider
      value={{
        filters,
        hasFilter,
        setHasFilter
      }}
    >
      {children}
    </Filters.Provider>
  );
}

export const useFilters = () => useContext(Filters);
